import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, RadioGroup, FormControlLabel, Radio, Typography } from '@mui/material';
import DsasButton from '../../../components/DsasButton/DsasButton.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkExistingPlayer, exportPlayers, fetchPlayers } from '../../../../api/index.js';
import PlayerSearch from './components/PlayerSearch';
import PlayerResults from './components/PlayerResults';
import PlayerCheckModal from '../create/PlayerCheckModal.js';
import Filters from '../../../components/Filters.js';
import { getUserProvider } from '../../../../actions/auth.js';
import useFusionMode from '../../../hooks/useFusionMode.js';
import useStyles from '../../../../styles/global-mui.js';
////////  ICONS /////////
import { faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { Can } from '../../../../permissions/Can.js';
import { PERMISSIONS } from '../../../../permissions/permissionKeys.js';
import { usePermissions } from '../../../../permissions/usePermissions.js';

const initialPlayer = {
  lastname: '',
  firstname: '',
  discipline: '',
  provider: getUserProvider(),
};

const sanitizeForFilename = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // accents
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

const buildPlayersFilename = (filters, scope) => {
  const provider = sanitizeForFilename(filters.provider || 'provider');
  const discipline = sanitizeForFilename(filters.discipline || 'discipline');
  const season = sanitizeForFilename(filters.season || 'season');
  const competition = sanitizeForFilename(filters.competitionName || filters.competition || 'competition');

  const teams = filters?.teams;
  const hasTeam =
    (Array.isArray(teams) && teams.length > 0) ||
    (!!teams && !Array.isArray(teams));

  if (scope === 'competition' || !hasTeam) {
    return `players_${provider}_${discipline}_${season}_${competition}_ALL_TEAMS.csv`;
  }

  const team = sanitizeForFilename(filters.teamName || teams || 'team');
  return `players_${provider}_${discipline}_${season}_${competition}_${team}.csv`;
};

const GetPlayers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('normal'); // Modes : 'normal', 'fusion'

  const [players, setPlayers] = useState();
  const [loading, setLoading] = useState(false);
  const [playerData, setPlayerData] = useState(initialPlayer);
  const [filters, setFilters] = useState({});
  const [existingPlayers, setExistingPlayers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckComplete, setIsCheckComplete] = useState(false);
  const [csvFilters, setCSVFilters] = useState({});
  
const [isExportModalOpen, setIsExportModalOpen] = useState(false);
const [exportScope, setExportScope] = useState('team'); // 'team' | 'competition'
const [exporting, setExporting] = useState(false);

  const { isFusionMode, FusionToggle, FusionZones, actions } = useFusionMode(players);

  const { getPermission } = usePermissions();
  const providerPermission = getPermission(PERMISSIONS.FILTER_PROVIDER);

  const filtersConfig = [
    { key: 'provider', name: 'provider', fullAccess:providerPermission },
    { key: 'discipline', name: 'discipline' },
    { key: 'season', name: 'season', isDateCustomizable : true },
    { key: 'competition', name: 'competition' },
    { key: 'teams', name: 'teams' },
  ];

  const searchParams = new URLSearchParams(location.search);
  const searchMode = searchParams.get('searchMode') || 'filters';

  useEffect(() => {
    if (searchMode === 'filters') {
      setPlayerData(initialPlayer);
    }

    setPlayers(undefined)
  }, [searchMode]);

  useEffect(() => {
    if (searchMode === 'simple') {
      const lastname = searchParams.get('lastname') || '';
      const firstname = searchParams.get('firstname') || '';
      const discipline = searchParams.get('discipline') || '';
      const provider = searchParams.get('provider') || getUserProvider();
      setPlayerData({ lastname, firstname, discipline, provider });
    }
  }, [location.search, searchMode]);

  useEffect(() => {
    if (!isValidCheckExistingPlayer()) return;

    handleCheckExistingPlayer();
  }, [playerData]);

  const handleApplyFilters = async (filters) => {
    //setPlayerData({ lastname, firstname, discipline, provider });
    setLoading(true);
    try {
      const response = await fetchPlayers(filters);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPlayerData((prev) => ({ ...prev, [name]: value }));

    const params = new URLSearchParams(location.search);
    params.set(name, value);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleCheckExistingPlayer = async () => {
    setPlayers([]);

    if (!isValidCheckExistingPlayer()) return;

    const { firstname, lastname, discipline } = playerData;
    const results = await checkExistingPlayer({ firstname, lastname, discipline });
    const similarPlayers = results.data;

    if (!similarPlayers.length) {
      setIsCheckComplete(true);
      setIsModalOpen(false);
    } else {
      const sameProviderPlayers = similarPlayers.filter((p) => p.provider === playerData.provider);
      const otherProviderPlayers = similarPlayers.filter((p) => p.provider !== playerData.provider);

      if (sameProviderPlayers.length) {
        setPlayers(sameProviderPlayers);
      } else if (otherProviderPlayers.length) {
        setExistingPlayers(otherProviderPlayers);
        setIsModalOpen(true);
      }
    }
  };

  const isValidCheckExistingPlayer = () => {

    return searchMode === 'simple' ? 
                      (playerData 
                      && playerData.lastname 
                      && playerData.discipline 
                      && playerData.lastname.length >= 3 )
                      :
                      (filters
                      && filters.discipline 
                      && filters.competition 
                      && filters.teams && filters.teams.length>0);
  }

  const isValidExportPlayers = () => {
    return (
      searchMode !== 'simple' &&
      csvFilters &&
      csvFilters.discipline &&
      csvFilters.season &&
      csvFilters.competition
      // provider optionnel selon besoin
    );
  };

  const hasTeamSelected = () => {
  // selon ton format: teams peut être string id ou array
  const t = csvFilters?.teams;
  if (!t) return false;
  if (Array.isArray(t)) return t.length > 0;
  return String(t).length > 0;
};

 const handleConfirmExportCSV = async () => {
  setExporting(true);
  try {
    // base export filters (avec champs UI)
    const base = { ...csvFilters };

    // 🔥 nettoyage des champs purement visuels
    const {
      competitionName,
      teamName,
      providerName,
      ...cleanFilters
    } = base;

    // si export compétition : on retire teams
    const payload =
      exportScope === 'competition'
        ? (() => {
            const { teams, ...rest } = cleanFilters;
            return rest;
          })()
        : cleanFilters;

    const response = await exportPlayers(payload);

    if (response.status !== 200) {
      throw new Error('Erreur lors du téléchargement du CSV');
    }

    const csvContent = await response.data;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const filename = buildPlayersFilename(base, exportScope);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsExportModalOpen(false);
  } catch (e) {
    console.error(e);
  } finally {
    setExporting(false);
  }
};

const handleOpenExportModal = () => {
  const defaultScope = hasTeamSelected() ? 'team' : 'competition';
  setExportScope(defaultScope);
  setIsExportModalOpen(true);
};

  const handleSelectOtherPlayer = (player) => {
    const copyPlayer = {
      ...player,
      provider: playerData.provider,
      teamsSeason: [],
      teams: [],
    };

    navigate(`/sports/players/create`, { state: { copyPlayer } });
    setIsCheckComplete(true);
    setIsModalOpen(false);
  };

  const handleConfirmNone = () => {
    setIsCheckComplete(true);
    setIsModalOpen(false);
    navigate(`/sports/players/create`, { state: { copyPlayer: { ...playerData, teamsSeason: [], teams: [] } } });
  };

  const removePlayerFromList = (id) => {
    setPlayers(s => s.filter(p => p._id !== id))
  }

  useEffect(() => {
  if (isExportModalOpen && exportScope === 'team' && !hasTeamSelected()) {
    setExportScope('competition');
  }
}, [csvFilters?.teams, isExportModalOpen, exportScope]);

  const classes = useStyles();

  return (
    <Box>

      {/*PAGE TITLE*/}
      <Box className='centered-medium-wrapper'>
        <h1 className='page-title'>Gestion des joueurs</h1>
      </Box>
      <Box className='medium-separator'></Box>

      {/*FUSION MODE BUTTON*/}
      <Box className='centered-medium-wrapper'>
        <Can permission={PERMISSIONS.SPORT_PLAYER_HOME_BUTTON_FUSION}>
          <FusionToggle />
        </Can>
      </Box>
      <Box className='medium-separator'></Box>

      {isFusionMode  && <FusionZones />}

      {/*CREATE PLAYER BUTTON*/}
      <Box className='centered-medium-wrapper'>
        <Can permission={PERMISSIONS.SPORT_PLAYER_HOME_BUTTON_CREATE}>
          <DsasButton label="Créer un joueur" icon={faUserPlus} onClick={() => navigate(`/sports/players/create`)} variant = 'secondary' />
        </Can>
      </Box>
      <Box className='medium-separator'></Box>

      {/*SEARCH SECTION*/}
      <Box className='centered-medium-wrapper'>
        {/*SEARCH BY NAME BUTTON*/}
        <DsasButton label="Rechercher par nom" onClick={() => navigate('?searchMode=simple')} variant = 'primarysub' focused = {searchMode === 'simple'} />
        {/*SEARCH BY TEAM BUTTON*/}
        <DsasButton label="Rechercher par équipe" onClick={() => navigate('?searchMode=filters')} variant = 'primarysub' focused = {searchMode !== 'simple'} />
      </Box>
      {/*SEARCH FILTERS*/}
      <Box className='padding-wrapper'>
      {searchMode === 'simple' ? (
        <PlayerSearch playerData={playerData} onChange={handleChange} className={classes.customInput} />
      ) : (
        <Filters filtersConfig={filtersConfig} onApplyFilters={handleApplyFilters} setCSVFilters={setCSVFilters} onReset={() => setPlayers([])} className={classes.customInput} setFiltersCallback={setFilters}/>
      )}
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <PlayerResults 
            showCreate={searchMode === 'simple' && isValidCheckExistingPlayer()} 
            handleSelectOtherPlayer={() => handleSelectOtherPlayer(playerData)}
            players={players} actions={actions} 
            canExport={isValidExportPlayers()}
            onExportCSV={handleOpenExportModal}
            removePlayerFromList={removePlayerFromList} 
            exportResult={searchMode !== 'simple'}
            showExport={!isFusionMode} showPicture={!isFusionMode}
            searchMode={searchMode}
            fieldsValidator={isValidCheckExistingPlayer}
            />
      )}

      <PlayerCheckModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        results={existingPlayers}
        onSelectOtherPlayer={handleSelectOtherPlayer}
        onConfirmNone={handleConfirmNone}
        actualProvider={playerData.provider}
      />


<Dialog open={isExportModalOpen} onClose={() => !exporting && setIsExportModalOpen(false)} fullWidth maxWidth="sm">
  <DialogTitle>Exporter les joueurs en CSV</DialogTitle>

  <DialogContent>
    {!isValidExportPlayers() ? (
      <Typography variant="body2">
        Sélectionne au minimum les filtres jusqu’à <b>Compétition</b> pour exporter.
      </Typography>
    ) : (
      <>
          <RadioGroup
          value={exportScope}
          onChange={(e) => setExportScope(e.target.value)}
        >
          {hasTeamSelected() && (
            <FormControlLabel
              value="team"
              control={<Radio />}
              label="Exporter les joueurs de l’équipe sélectionnée"
            />
          )}

          <FormControlLabel
            value="competition"
            control={<Radio />}
            label="Exporter les joueurs de toutes les équipes de la compétition"
          />
        </RadioGroup>
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          Fichier : <b>{buildPlayersFilename(csvFilters, exportScope)}</b>
        </Typography>
      </>
    )}
  </DialogContent>

      <DialogActions>
        <Button disabled={exporting} onClick={() => setIsExportModalOpen(false)}>
          Annuler
        </Button>
        <Button
          variant="contained"
          disabled={
              !isValidExportPlayers() ||
              exporting ||
              (exportScope === 'team' && !hasTeamSelected())
            }
          onClick={handleConfirmExportCSV}
        >
          {exporting ? 'Export en cours…' : 'Exporter'}
        </Button>
      </DialogActions>
    </Dialog>

    </Box>
  );
};

export default GetPlayers;
